import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="Survey results"` — plan unit P14-03, one campaign's survey results.
 *
 * AMENDED 2026-10-03 (design §16.7.2 AS BUILT 2026-10-03). The screen keeps its design and its label —
 * the back link, the kicker and title, Download and the disabled Share a link, the chip row, the
 * figures, the note strip, By question / By person, the repeat card and the footnote — and reads the
 * survey interviewer's own answers (`survey_answer`) instead of survey rounds, which nothing ever
 * wrote. Its body is `SurveyResultBody`, the same one the VoiceAgent page's Results tab draws, with the
 * campaign fixed: everything on it is the conversations THIS campaign placed. The chip row offers this
 * campaign and a way to every conversation of the survey (the Results tab).
 *
 * The filters (window, language, arm, test conversations) and the rendering are in the URL, so a
 * filtered page is a link. The download is the page: same campaign, same filters.
 *
 * WHAT THE DESIGN DRAWS THAT IS NOT BUILT, AND WHY.
 *
 *   · **"Share a link".** A shareable read-only link is a grant, with a lifetime and a revocation,
 *     and no operation issues one. Every filter is in the URL, so a colleague who can already open
 *     this workspace can be sent this page as it stands.
 *   · **"Both surveys", the round chip.** There are no rounds: one survey's answers are read across
 *     every campaign on the VoiceAgent's Results tab, which the second chip opens.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Button, Icon } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { SurveyDownloadDialog } from "./survey-download-dialog.js";
import { SurveyResultBody, type SurveyResultSearchView, type SurveyResultViewMode } from "./survey-result-body.js";
import { countText, type RespondentFilterValue } from "./survey-result-copy.js";
import { GLYPH } from "../../shell/glyph.js";
import { useGateway } from "../../api/provider.js";
import { useMemberCan } from "../../shell/member-capability.js";
import { campaignQuery, surveyResultQuery } from "../../api/query.js";
import { apiErrorText } from "../../api/api-error.js";
import type { SurveyResultFilterView } from "../../api/gateway.js";

import "./survey-result.css";

export type { SurveyResultViewMode } from "./survey-result-body.js";

/** The URL's filter, without the campaign: the campaign is the route's own. */
export type CampaignSurveyFilterView = Omit<SurveyResultFilterView, "campaignId">;

export interface SurveyResultScreenProps {
  readonly campaignId: string;
  readonly filter: CampaignSurveyFilterView;
  readonly view: SurveyResultViewMode;
  readonly status: RespondentFilterValue;
  onSearch(next: Partial<CampaignSurveyFilterView & { readonly view: SurveyResultViewMode; readonly status: RespondentFilterValue }>): void;
}

export function SurveyResultScreen(props: SurveyResultScreenProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const [downloadOpen, setDownloadOpen] = useState(false);
  // 2026-11-03: every role that reads the results may download them (the API's roles; a Billing member may not).
  const member = useMemberCan();
  const campaign = useQuery(campaignQuery(gateway, props.campaignId));
  const filter: SurveyResultFilterView = { ...props.filter, campaignId: props.campaignId };
  const voiceAgentId = campaign.data?.voiceAgentId ?? "";
  const result = useQuery({ ...surveyResultQuery(gateway, voiceAgentId, filter), enabled: voiceAgentId !== "" });

  const backLink = (
    <Link to="/campaigns/$campaignId" params={{ campaignId: props.campaignId }} className="c-backlink srv-back">
      <Icon glyph={GLYPH.arrowLeft} size="sm" />
      {portalText("operations", "back_to_the_campaign_page_db348826")}</Link>
  );

  if (campaign.isError) {
    return (
      <section className="c-page srv-page">
        {backLink}
        <h1 className="srv-title">{portalText("operations", "survey_results_5774f95e")}</h1>
        <p className="c-note c-note-critical">
          {portalText("operations", "we_could_not_load_this_campaign_so_nothing_is_shown_rather_4b87fc78")}{" "}
          {apiErrorText(campaign.error, "")}
        </p>
      </section>
    );
  }
  if (campaign.data === undefined) {
    return (
      <section className="c-page srv-page">
        <p className="c-note">{portalText("operations", "finding_the_campaign_0638162c")}</p>
      </section>
    );
  }

  const kicker =
    result.data === undefined
      ? campaign.data.name
      : portalText("operations", "value_value_completed_of_value_conversations_b1f4ed0f", { v0: String(campaign.data.name), v1: String(countText(result.data.completedCount)), v2: String(countText(
          result.data.conversationCount,
        )) });

  return (
    <section className="c-page srv-page">
      {backLink}

      <div className="c-headline-row srv-headline">
        <div>
          <p className="srv-kicker">{kicker}</p>
          <h1 className="srv-title">{portalText("operations", "survey_results_5774f95e")}</h1>
        </div>
        <div className="c-survey-actions">
          <Button
            variant="secondary"
            disabled={result.data === undefined || !member.can("survey_result.export")}
            title={member.whyNot("survey_result.export") ?? undefined}
            onClick={() => {
              setDownloadOpen(true);
            }}
          >
            <Icon glyph={GLYPH.downloadSimple} size="md" />
            {portalText("operations", "download_a479c9c3")}</Button>
          {/* Drawn where the design draws it, and disabled: no operation issues a read-only share
              grant yet (see the header). The page's URL already carries every filter. */}
          <Button
            variant="primary"
            disabled
            title={portalText("operations", "soon_until_then_this_page_s_address_already_opens_these_re_fe19d42a")}
          >
            <Icon glyph={GLYPH.shareNetwork} size="md" />
            {portalText("operations", "share_a_link_130714ce")}<span className="c-survey-soon">{portalText("operations", "soon_3f934e4f")}</span>
          </Button>
        </div>
      </div>

      <SurveyResultBody
        voiceAgentId={campaign.data.voiceAgentId}
        campaignScope={{ campaignId: props.campaignId, name: campaign.data.name }}
        filter={filter}
        view={props.view}
        outcome={props.status}
        onSearch={(next: Partial<SurveyResultSearchView>) => {
          const { campaignId: _campaignId, ...rest } = next.filter ?? filter;
          props.onSearch({
            ...(next.filter === undefined ? {} : rest),
            ...(next.view === undefined ? {} : { view: next.view }),
            ...(next.outcome === undefined ? {} : { status: next.outcome }),
          });
        }}
      />

      <SurveyDownloadDialog
        open={downloadOpen}
        voiceAgentId={campaign.data.voiceAgentId}
        filter={filter}
        conversationCount={result.data?.conversationCount ?? 0}
        onClose={() => {
          setDownloadOpen(false);
        }}
      />
    </section>
  );
}
